import React from 'react';
import { SearchOutlined, BellOutlined, MoonOutlined, SunOutlined, MenuOutlined } from '@ant-design/icons';
import { IconButton } from './IconButton';
import type { AppHeaderUser } from './types';
import styles from './AppHeader.module.css';

export interface AppHeaderProps {
  logo?: React.ReactNode;
  onMenu?: () => void;
  onSearchClick?: () => void;
  searchPlaceholder?: string;
  signalCount?: number;
  onSignalsClick?: () => void;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  user?: AppHeaderUser;
  onUserClick?: () => void;
  /** When set, replaces the default user avatar button (e.g. dropdown menu). */
  userSlot?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  logo = null,
  onMenu,
  onSearchClick,
  searchPlaceholder = 'Search domains, artifacts, signals…',
  signalCount = 0,
  onSignalsClick,
  theme = 'light',
  onToggleTheme,
  user,
  onUserClick,
  userSlot,
  actions,
  className,
}) => (
  <header className={`${styles.header} ${className ?? ''}`} data-testid="app-header">
    <div className={styles.headerStart}>
      {onMenu ? (
        <IconButton label="Open navigation menu" onClick={onMenu} className={styles.menuBtn}>
          <MenuOutlined />
        </IconButton>
      ) : null}
      {logo ? <div className={styles.logoWrap}>{logo}</div> : null}
    </div>

    {onSearchClick ? (
      <div className={styles.headerCenter}>
        <button type="button" className={styles.searchBtn} onClick={onSearchClick}>
          <SearchOutlined />
          <span className={styles.searchPlaceholder}>{searchPlaceholder}</span>
          <kbd className={styles.searchKbd}>⌘K</kbd>
        </button>
      </div>
    ) : (
      <div className={styles.headerCenter} aria-hidden="true" />
    )}

    <div className={styles.headerEnd}>
      <div className={styles.actions}>
        {actions}
        {onToggleTheme ? (
          <IconButton label="Toggle theme" onClick={onToggleTheme}>
            {theme === 'dark' ? <SunOutlined /> : <MoonOutlined />}
          </IconButton>
        ) : null}
        {onSignalsClick ? (
          <IconButton label="Signals" badge={signalCount} onClick={onSignalsClick}>
            <BellOutlined />
          </IconButton>
        ) : null}
        {userSlot ??
          (user && onUserClick ? (
            <button type="button" className={styles.userBtn} aria-label="Account" onClick={onUserClick}>
              <span className={styles.userInitials}>{user.initials}</span>
            </button>
          ) : null)}
      </div>
    </div>
  </header>
);
